<template>
  <div class="tab-pane fade show active">
    <LoadingIndicatorComponent
      v-if="loading"
      message="Loading live dashboard..."
    />
    <div v-else>
      <div v-if="error" class="alert alert-danger">{{ error }}</div>

      <!-- Live Price Cards -->
      <div class="row g-3 mb-4">
        <div v-if="snapshots.length === 0" class="col-12">
          <div class="alert alert-info mb-0">No price data available yet.</div>
        </div>
        <div
          v-for="snapshot in snapshots"
          :key="`${snapshot.name}|||${snapshot.source}`"
          class="col-md-4"
        >
          <div class="card h-100">
            <div class="card-header">
              <h6 class="mb-0">{{ snapshot.label }}</h6>
              <small class="text-muted">
                {{ snapshot.latest_date }}
                <span v-if="snapshot.currency">
                  · {{ snapshot.currency.toUpperCase() }}/MWh
                </span>
              </small>
            </div>
            <div class="card-body p-2">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Vintage</th>
                    <th class="text-end">Bid</th>
                    <th class="text-end">Ask</th>
                    <th class="text-end">Mid</th>
                    <th class="text-end">Δ Mid</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in snapshot.prices" :key="row.vintage ?? '-'">
                    <td>{{ row.vintage ?? "-" }}</td>
                    <td class="text-end">{{ formatPriceOrDash(row.bid) }}</td>
                    <td class="text-end">{{ formatPriceOrDash(row.ask) }}</td>
                    <td class="text-end">{{ formatPriceOrDash(row.mid) }}</td>
                    <td class="text-end" :class="changeClass(snapshot, row)">
                      {{ formatChange(snapshot, row) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="mt-2 border-top pt-2">
                <small class="text-muted d-block mb-1">
                  Bid/ask — last year (solid = ask, dashed = bid)
                </small>
                <MiniBidAskChartComponent
                  :series="seriesForProduct(snapshot)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Trades -->
      <div class="card">
        <div class="card-header">
          <h6 class="mb-0">Recent Trades — Last 30 Days</h6>
        </div>
        <div class="card-body">
          <LoadingIndicatorComponent v-if="tradesLoading" />
          <div v-else-if="tradesError" class="alert alert-danger mb-0">
            {{ tradesError }}
          </div>
          <div v-else-if="trades.length === 0" class="alert alert-info mb-0">
            No trades in the last 30 days.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm table-striped align-middle mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Rule</th>
                  <th>Technology</th>
                  <th>Production</th>
                  <th class="text-end">Price</th>
                  <th class="text-end">Volume</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="trade in trades" :key="trade.id">
                  <td class="text-nowrap">
                    {{ trade.transaction_date.slice(0, 10) }}
                  </td>
                  <td>{{ trade.product ?? "-" }}</td>
                  <td>{{ trade.product_rule ?? "-" }}</td>
                  <td>{{ trade.technology ?? "-" }}</td>
                  <td>{{ formatProduction(trade) }}</td>
                  <td class="text-end text-nowrap">
                    {{ trade.price_per_unit }}
                    {{ trade.currency?.toUpperCase() ?? "" }}
                  </td>
                  <td class="text-end">{{ trade.volume }}</td>
                  <td>{{ trade.source ?? "-" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { onMounted, ref } from "vue";

import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import MiniBidAskChartComponent from "@/market_insights/admin/MiniBidAskChartComponent.vue";
import { formatPriceOrDash } from "@/market_insights/admin/eacPricingFormat";
import type { DailyPriceSeriesPoint } from "@/market_insights/admin/eacPricingTypes";

interface DailyPriceRow {
  vintage: string | null;
  bid: number | null;
  ask: number | null;
  mid: number | null;
}

interface DailyPriceSnapshot {
  name: string;
  source: string;
  label: string;
  currency: string | null;
  latest_date: string;
  prices: DailyPriceRow[];
  previous_date: string | null;
  previous_prices: Record<string, DailyPriceRow>;
}

interface Trade {
  id: number;
  transaction_date: string;
  product: string | null;
  product_rule: string | null;
  technology: string | null;
  production_years: number[] | null;
  production_period: string | null;
  price_per_unit: string;
  currency: string | null;
  volume: string;
  source: string | null;
}

const { globalErrorHandler } = useGlobalHandlers();

const snapshots = ref<DailyPriceSnapshot[]>([]);
const dailySeries = ref<DailyPriceSeriesPoint[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

const trades = ref<Trade[]>([]);
const tradesLoading = ref(false);
const tradesError = ref<string | null>(null);

const seriesForProduct = (
  snapshot: DailyPriceSnapshot
): DailyPriceSeriesPoint[] =>
  dailySeries.value.filter(
    (point) => point.name === snapshot.name && point.source === snapshot.source
  );

const formatProduction = (trade: Trade): string => {
  if (trade.production_years?.length) {
    return trade.production_years.join(", ");
  }
  return trade.production_period ?? "-";
};

// Backend keys previous_prices by vintage, with null vintages under "null"
// (matching JSON serialization of a null key)
const NULL_VINTAGE_KEY = "null";

const midChangePct = (
  snapshot: DailyPriceSnapshot,
  row: DailyPriceRow
): number | null => {
  const previous = snapshot.previous_prices[row.vintage ?? NULL_VINTAGE_KEY];
  if (
    !previous ||
    previous.mid === null ||
    previous.mid === 0 ||
    row.mid === null
  ) {
    return null;
  }
  return ((row.mid - previous.mid) / previous.mid) * 100;
};

const formatChange = (
  snapshot: DailyPriceSnapshot,
  row: DailyPriceRow
): string => {
  const change = midChangePct(snapshot, row);
  if (change === null) {
    return "-";
  }
  const sign = change > 0 ? "+" : "";
  return `${sign}${change.toFixed(1)}%`;
};

const changeClass = (
  snapshot: DailyPriceSnapshot,
  row: DailyPriceRow
): string => {
  const change = midChangePct(snapshot, row);
  if (change === null || change === 0) {
    return "text-muted";
  }
  return change > 0 ? "text-success" : "text-danger";
};

const fetchDashboard = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await goSolidApi.get(
      "/api/admin/eac-pricing/live-dashboard?days=365"
    );
    snapshots.value = response.data.data.snapshots;
    dailySeries.value = response.data.data.daily_series;
  } catch (err: any) {
    error.value = "Failed to fetch live dashboard data";
    globalErrorHandler(err);
  } finally {
    loading.value = false;
  }
};

const fetchTrades = async () => {
  tradesLoading.value = true;
  tradesError.value = null;
  try {
    const response = await goSolidApi.get(
      "/api/admin/eac-trade/trades?days=30&limit=15"
    );
    trades.value = response.data.trades;
  } catch (err: any) {
    tradesError.value = "Failed to fetch recent trades";
    globalErrorHandler(err);
  } finally {
    tradesLoading.value = false;
  }
};

onMounted(async () => {
  await Promise.all([fetchDashboard(), fetchTrades()]);
});
</script>
